import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: '90d', label: 'Last 90 Days' },
  { key: 'mtd', label: 'Month-to-Date' },
  { key: 'ytd', label: 'Year-to-Date' },
  { key: 'all', label: 'All Time' },
];

const isoDate = (d) => d.toISOString().slice(0, 10);

const computePreset = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case 'today':
      return { date_from: isoDate(today), date_to: isoDate(today) };
    case '7d': {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { date_from: isoDate(start), date_to: isoDate(today) };
    }
    case '30d': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { date_from: isoDate(start), date_to: isoDate(today) };
    }
    case '90d': {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      return { date_from: isoDate(start), date_to: isoDate(today) };
    }
    case 'mtd': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { date_from: isoDate(start), date_to: isoDate(today) };
    }
    case 'ytd': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { date_from: isoDate(start), date_to: isoDate(today) };
    }
    case 'all':
    default:
      return { date_from: '', date_to: '' };
  }
};

/**
 * Reusable CSV export dialog.
 *
 * Props:
 *  - open, onOpenChange
 *  - entity: 'orders' | 'invoices' | 'clients' | 'products' | 'credit-memos' | 'payments'
 *  - title: string
 *  - description: string
 *  - supportsDateRange: boolean — show the date-range section
 *  - defaultPreset: 'all' | 'today' | '7d' | '30d' | '90d' | 'mtd' | 'ytd' (default '30d' if dates supported)
 *  - extraFilters: object — additional query params (status, client_id, search, etc.) to forward
 *  - extraFilterSummary: array of {label, value} chips shown read-only ("honoring active filters")
 */
export const ExportDialog = ({
  open,
  onOpenChange,
  entity,
  title,
  description,
  supportsDateRange = true,
  defaultPreset = '30d',
  extraFilters = {},
  extraFilterSummary = [],
}) => {
  const [columns, setColumns] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loadingCols, setLoadingCols] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [preset, setPreset] = useState(supportsDateRange ? defaultPreset : 'all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Apply default preset on open
  useEffect(() => {
    if (open && supportsDateRange) {
      const { date_from, date_to } = computePreset(defaultPreset);
      setPreset(defaultPreset);
      setDateFrom(date_from);
      setDateTo(date_to);
    }
  }, [open, defaultPreset, supportsDateRange]);

  // Load column registry on open
  useEffect(() => {
    if (!open || !entity) return;
    let cancelled = false;
    (async () => {
      setLoadingCols(true);
      try {
        const data = await adminService.getExportColumns(entity);
        if (cancelled) return;
        setColumns(data.columns || []);
        setSelectedKeys(data.default_columns || []);
      } catch {
        if (cancelled) return;
        toast.error('Failed to load export columns');
      } finally {
        if (!cancelled) setLoadingCols(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, entity]);

  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);
  const allSelected = selectedKeys.length === allKeys.length && allKeys.length > 0;
  const noneSelected = selectedKeys.length === 0;

  const toggleColumn = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handlePresetClick = (key) => {
    setPreset(key);
    const { date_from, date_to } = computePreset(key);
    setDateFrom(date_from);
    setDateTo(date_to);
  };

  const handleExport = async () => {
    if (noneSelected) {
      toast.error('Select at least one column to export');
      return;
    }
    setExporting(true);
    try {
      // Preserve user-picked column order; selectedKeys already reflects order of clicks,
      // but we want canonical (registry) order — keep the order columns appear in the picker.
      const orderedSelected = allKeys.filter((k) => selectedKeys.includes(k));
      const params = {
        ...extraFilters,
        columns: orderedSelected.join(','),
      };
      if (supportsDateRange) {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const filename = await adminService.downloadExport(entity, params);
      toast.success(`Exported ${filename}`);
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid={`export-dialog-${entity}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-amber-600" />
            {title || `Export ${entity}`}
          </DialogTitle>
          <DialogDescription>
            {description ||
              'Choose columns and date range, then download a CSV file. Active filters are honored.'}
          </DialogDescription>
        </DialogHeader>

        {/* Active filters chips */}
        {extraFilterSummary.length > 0 && (
          <div
            className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2"
            data-testid="export-active-filters"
          >
            <p className="text-xs font-medium text-amber-800 uppercase tracking-wide">
              Active filters honored
            </p>
            <div className="flex flex-wrap gap-2">
              {extraFilterSummary.map((f) => (
                <Badge
                  key={`${f.label}-${f.value}`}
                  variant="outline"
                  className="bg-white text-amber-700 border-amber-300"
                >
                  {f.label}: {f.value}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Date range */}
        {supportsDateRange && (
          <div className="space-y-2" data-testid="export-date-range">
            <Label className="text-sm font-medium">Date Range</Label>
            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((p) => (
                <Button
                  key={p.key}
                  type="button"
                  variant={preset === p.key ? 'default' : 'outline'}
                  size="sm"
                  className={preset === p.key ? 'bg-amber-500 hover:bg-amber-600' : ''}
                  onClick={() => handlePresetClick(p.key)}
                  data-testid={`export-preset-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <Label htmlFor="export-date-from" className="text-xs text-gray-500">
                  From
                </Label>
                <Input
                  id="export-date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setPreset('custom');
                    setDateFrom(e.target.value);
                  }}
                  data-testid="export-date-from"
                />
              </div>
              <div>
                <Label htmlFor="export-date-to" className="text-xs text-gray-500">
                  To
                </Label>
                <Input
                  id="export-date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setPreset('custom');
                    setDateTo(e.target.value);
                  }}
                  data-testid="export-date-to"
                />
              </div>
            </div>
          </div>
        )}

        {/* Columns */}
        <div className="space-y-2" data-testid="export-columns">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              Columns
              <span className="ml-2 text-xs font-normal text-gray-500">
                {selectedKeys.length} of {columns.length} selected
              </span>
            </Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedKeys(allKeys)}
                disabled={allSelected || loadingCols}
                data-testid="export-select-all"
              >
                Select all
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedKeys([])}
                disabled={noneSelected || loadingCols}
                data-testid="export-clear-all"
              >
                Clear
              </Button>
            </div>
          </div>
          <ScrollArea className="h-[200px] rounded-md border p-3">
            {loadingCols ? (
              <div className="text-sm text-gray-400 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading columns…
              </div>
            ) : columns.length === 0 ? (
              <div className="text-sm text-gray-400">No columns available.</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {columns.map((col) => {
                  const checked = selectedKeys.includes(col.key);
                  return (
                    <label
                      key={col.key}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded p-1"
                      data-testid={`export-col-${col.key}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleColumn(col.key)}
                      />
                      <span className="truncate">{col.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={exporting}
            data-testid="export-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || noneSelected || loadingCols}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="export-download"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting…
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" /> Download CSV
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
